import React, { useEffect, useState, useCallback } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { PERFIS_PERMISSOES, GRUPOS_PERMISSOES } from '../utils/permissoes';

const PERFIL_LABEL = { admin: 'Admin', gerente: 'Gerente', operador: 'Operador' };
const PERFIL_COR = { admin: '#7c3aed', gerente: '#2563eb', operador: '#16a34a' };

const FORM_VAZIO = { nome: '', email: '', senha: '', role: 'admin', permissoes: { ...PERFIS_PERMISSOES.admin } };

function BadgePerfil({ role }) {
  return (
    <span style={{
      fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20,
      background: PERFIL_COR[role] + '18', color: PERFIL_COR[role], border: `1px solid ${PERFIL_COR[role]}33`,
    }}>{PERFIL_LABEL[role] || role}</span>
  );
}

function MatrizPermissoes({ permissoes, onChange, bloqueado }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 8 }}>
      {GRUPOS_PERMISSOES.map(g => (
        <div key={g.grupo}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {g.grupo}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, paddingLeft: 8 }}>
            {g.itens.map(item => {
              const depBloqueado = item.dependeDe && !permissoes[item.dependeDe];
              const desabilitado = bloqueado || depBloqueado;
              return (
                <label key={item.chave} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: desabilitado ? 'not-allowed' : 'pointer', fontSize: 13 }}>
                  <input
                    type="checkbox"
                    checked={!!permissoes[item.chave]}
                    onChange={e => !desabilitado && onChange(item.chave, e.target.checked)}
                    disabled={desabilitado}
                    style={{ width: 15, height: 15 }}
                  />
                  <span style={{ color: desabilitado ? 'var(--text-muted)' : 'var(--text-primary)' }}>
                    {item.label}
                    {depBloqueado && <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 4 }}>(requer "Visualizar")</span>}
                  </span>
                </label>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function Usuarios() {
  const [usuarios, setUsuarios] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [modalAberto, setModalAberto] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(FORM_VAZIO);
  const [salvando, setSalvando] = useState(false);
  const [tenantInfo, setTenantInfo] = useState(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const [resUsuarios, resTenant] = await Promise.all([
        api.get('/usuarios'),
        api.get('/tenant/status'),
      ]);
      setUsuarios(resUsuarios.data);
      setTenantInfo(resTenant.data);
    } catch { }
    setCarregando(false);
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  function abrirNovo() {
    setEditando(null);
    setForm(FORM_VAZIO);
    setModalAberto(true);
  }

  function abrirEditar(u) {
    setEditando(u);
    setForm({
      nome: u.nome,
      email: u.email,
      senha: '',
      role: u.role,
      permissoes: { ...u.permissoes },
    });
    setModalAberto(true);
  }

  function handlePerfilChange(role) {
    setForm(f => ({
      ...f,
      role,
      permissoes: role === 'admin' ? { ...PERFIS_PERMISSOES.admin } : { ...PERFIS_PERMISSOES[role] },
    }));
  }

  function handlePermissaoChange(chave, valor) {
    setForm(f => {
      const novas = { ...f.permissoes, [chave]: valor };
      GRUPOS_PERMISSOES.forEach(g => g.itens.forEach(item => {
        if (!valor && item.dependeDe === chave) novas[item.chave] = false;
        if (valor && item.chave === chave && item.exclusivoCom) {
          item.exclusivoCom.forEach(c => { novas[c] = false; });
        }
      }));
      return { ...f, permissoes: novas };
    });
  }

  async function salvar() {
    if (!form.nome.trim() || !form.email.trim()) { toast.error('Nome e e-mail são obrigatórios.'); return; }
    if (!editando && !form.senha.trim()) { toast.error('Informe uma senha temporária.'); return; }
    setSalvando(true);
    try {
      const payload = { nome: form.nome, email: form.email, role: form.role, permissoes: form.permissoes };
      if (form.senha.trim()) payload.senha = form.senha;

      if (editando) {
        await api.put(`/usuarios/${editando.usuarioid}`, payload);
        toast.success('Usuário atualizado!');
      } else {
        await api.post('/usuarios', payload);
        toast.success('Usuário criado!');
      }
      setModalAberto(false);
      carregar();
    } catch (err) {
      toast.error(err.response?.data?.mensagem || 'Erro ao salvar usuário.');
    }
    setSalvando(false);
  }

  async function toggleAtivo(u) {
    try {
      await api.put(`/usuarios/${u.usuarioid}/toggle-ativo`);
      toast.success(u.ativo ? 'Usuário desativado.' : 'Usuário ativado.');
      carregar();
    } catch (err) {
      toast.error(err.response?.data?.mensagem || 'Erro ao alterar status.');
    }
  }

  const temControleUsuarios = tenantInfo?.funcionalidades?.controle_usuarios ?? true;
  const limiteUsuarios = tenantInfo?.limites?.usuarios;
  const limiteAtingido = limiteUsuarios?.limite !== null && limiteUsuarios?.uso >= limiteUsuarios?.limite;

  if (carregando) return <div style={{ padding: 40, color: '#6b7280' }}>Carregando...</div>;

  return (
    <div>
      <div className="page-header">
        <h2 style={{ margin: 0 }}>👤 Usuários</h2>
        {!limiteAtingido && (
          <button className="btn-primary" onClick={abrirNovo}>
            + Novo Usuário
          </button>
        )}
      </div>
      {limiteAtingido && (
        <div style={{ background: '#fef3c7', border: '1px solid #d97706', borderRadius: 8, padding: '10px 16px', marginBottom: 16, fontSize: 13, color: '#92400e' }}>
          Limite de <strong>{limiteUsuarios.limite} usuários</strong> do seu plano atingido. Faça upgrade para adicionar mais.
        </div>
      )}

      <div className="card">
        <table>
          <thead>
            <tr>
              <th>Nome</th>
              <th>E-mail</th>
              <th>Perfil</th>
              <th>Status</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {usuarios.map(u => (
              <tr key={u.usuarioid}>
                <td data-label="Nome"><strong>{u.nome}</strong></td>
                <td data-label="E-mail" style={{ color: '#6b7280', fontSize: 13 }}>{u.email}</td>
                <td data-label="Perfil"><BadgePerfil role={u.role} /></td>
                <td data-label="Status">
                  <span style={{
                    fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 20,
                    background: u.ativo ? '#dcfce7' : '#f3f4f6',
                    color: u.ativo ? '#16a34a' : '#6b7280',
                  }}>
                    {u.ativo ? 'Ativo' : 'Inativo'}
                  </span>
                </td>
                <td data-label="Ações">
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn-secondary" style={{ fontSize: 12 }} onClick={() => abrirEditar(u)}>
                      Editar
                    </button>
                    <button
                      className={u.ativo ? 'btn-danger' : 'btn-secondary'}
                      style={{ fontSize: 12 }}
                      onClick={() => toggleAtivo(u)}
                    >
                      {u.ativo ? 'Desativar' : 'Ativar'}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {usuarios.length === 0 && (
              <tr><td colSpan={5} style={{ textAlign: 'center', color: '#999' }}>Nenhum usuário encontrado</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {modalAberto && (
        <div className="modal-overlay" onClick={() => setModalAberto(false)}>
          <div className="modal-box" onClick={e => e.stopPropagation()} style={{ maxWidth: 540, maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ marginBottom: 20 }}>{editando ? 'Editar Usuário' : 'Novo Usuário'}</h3>

            <div style={{ display: 'flex', gap: 12, marginBottom: 4, flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 180px' }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Nome *</label>
                <input value={form.nome} onChange={e => setForm(f => ({ ...f, nome: e.target.value }))} placeholder="Nome completo" style={{ marginBottom: 0 }} />
              </div>
              <div style={{ flex: '1 1 180px' }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>E-mail *</label>
                <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="email@exemplo.com" style={{ marginBottom: 0 }} autoComplete="off" />
              </div>
            </div>

            <div style={{ display: 'flex', gap: 12, marginBottom: 4, marginTop: 12, flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 180px' }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>
                  {editando ? 'Nova senha (deixe em branco para manter)' : 'Senha temporária *'}
                </label>
                <input type="password" value={form.senha} onChange={e => setForm(f => ({ ...f, senha: e.target.value }))} placeholder="••••••••" style={{ marginBottom: 0 }} autoComplete="new-password" />
              </div>
              {temControleUsuarios && (
                <div style={{ flex: '1 1 140px' }}>
                  <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Perfil base</label>
                  <select value={form.role} onChange={e => handlePerfilChange(e.target.value)} style={{ marginBottom: 0 }}>
                    <option value="admin">Admin</option>
                    <option value="gerente">Gerente</option>
                    <option value="operador">Operador</option>
                  </select>
                </div>
              )}
            </div>

            {temControleUsuarios && (
              <div style={{ marginTop: 20, borderTop: '1px solid #e5e7eb', paddingTop: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>Permissões</div>
                {form.role === 'admin' && (
                  <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>
                    Administradores têm acesso total e não podem ter permissões reduzidas.
                  </p>
                )}
                <MatrizPermissoes
                  permissoes={form.permissoes}
                  onChange={handlePermissaoChange}
                  bloqueado={form.role === 'admin'}
                />
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 24 }}>
              <button className="btn-secondary" onClick={() => setModalAberto(false)}>Cancelar</button>
              <button className="btn-primary" onClick={salvar} disabled={salvando}>
                {salvando ? 'Salvando...' : (editando ? 'Salvar alterações' : 'Criar usuário')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Usuarios;
